import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

// Syntax: [wrong|correct] or [wrong|correct1|correct2] anywhere in a sentence.
// The first pipe-segment is the wrong word shown to the student; the rest are
// accepted corrections. Multiple mistakes per sentence are supported.
const MISTAKE_REGEX = /\[([^\]]+)\]/g;

const splitIntoTokens = (str) => {
  // Preserve whitespace and punctuation as their own tokens so we can render
  // the sentence with clickable words while keeping the exact spacing.
  const parts = [];
  const re = /(\s+|[.,!?;:()"'’‘“”\-–—]+)/g;
  let idx = 0;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m.index > idx) parts.push({ type: 'word', text: str.slice(idx, m.index) });
    parts.push({ type: 'sep', text: m[0] });
    idx = m.index + m[0].length;
  }
  if (idx < str.length) parts.push({ type: 'word', text: str.slice(idx) });
  return parts;
};

// Turn `She [go|goes] to school every day.` into a flat token stream where
// mistake tokens carry their accepted corrections. Non-mistake segments are
// further split into word/separator tokens so each word is individually
// clickable in the student view.
export const parseFixMistakeSentence = (rawText) => {
  const tokens = [];
  const src = rawText || '';
  let lastIdx = 0;
  let mistakeIdx = 0;
  MISTAKE_REGEX.lastIndex = 0;
  let match;
  while ((match = MISTAKE_REGEX.exec(src)) !== null) {
    if (match.index > lastIdx) {
      splitIntoTokens(src.slice(lastIdx, match.index)).forEach((t) => tokens.push(t));
    }
    const parts = match[1].split('|').map((s) => s.trim()).filter(Boolean);
    const wrong = parts[0] || '';
    const corrections = parts.slice(1);
    tokens.push({
      type: 'mistake',
      text: wrong,
      corrections,
      mistakeIdx,
    });
    mistakeIdx += 1;
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < src.length) {
    splitIntoTokens(src.slice(lastIdx)).forEach((t) => tokens.push(t));
  }
  return { tokens, mistakeCount: mistakeIdx };
};

const normalize = (s) => (s || '')
  .toString()
  .toLowerCase()
  .trim()
  .replace(/\s+/g, ' ')
  .replace(/[.,!?;:"'’‘“”()[\]{}\-–—]/g, '');

const FixMistakeElement = ({
  element,
  isEditor = false,
  studentView = false,
  answers: externalAnswers = null,
  showResult = false,
  perMistakeResults = null,
  onAnswersChange,
  onCheck,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    question = '',
    sentences = [],
    fontFamily = 'Inter',
    fontSize = 18,
    textColor = '#1f2937',
    buttonColor = '#2A4D25',
    highlightColor = '#2A4D25',
    actionBarColor = '#f9fafb',
  } = content || {};

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  // answers shape: { [sentenceId]: { [mistakeIdx]: "typed value" } }
  const [answers, setAnswers] = useState(externalAnswers || {});
  useEffect(() => {
    if (externalAnswers) setAnswers(externalAnswers);
  }, [externalAnswers]);

  // Which mistake token is currently in edit mode (student clicked it).
  const [editing, setEditing] = useState({ sentenceId: null, mistakeIdx: null });

  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: sentences.length,
    onAutoGrow,
  });

  const parsed = sentences.map((s) => ({ ...s, parsed: parseFixMistakeSentence(s.text || '') }));

  const updateAnswers = (next) => {
    setAnswers(next);
    onAnswersChange?.(next);
  };

  const handleWordClick = (sentenceId, mistakeIdx) => {
    if (isEditor || disabled || showResult) return;
    setEditing({ sentenceId, mistakeIdx });
  };

  const handleInputChange = (sentenceId, mistakeIdx, value) => {
    const next = {
      ...answers,
      [sentenceId]: { ...(answers[sentenceId] || {}), [mistakeIdx]: value },
    };
    updateAnswers(next);
  };

  const handleInputBlur = () => {
    setEditing({ sentenceId: null, mistakeIdx: null });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleInputBlur();
    }
  };

  const allTouched = parsed.length > 0
    && parsed.every((s) => {
      if (s.parsed.mistakeCount === 0) return true;
      const bag = answers[s.id] || {};
      for (let i = 0; i < s.parsed.mistakeCount; i += 1) {
        const v = (bag[i] ?? '').toString().trim();
        if (!v) return false;
      }
      return true;
    });

  const allCorrect = perMistakeResults
    && Object.keys(perMistakeResults).length > 0
    && Object.values(perMistakeResults).every((arr) => (arr || []).every((v) => v === true));

  const hideChrome = !isEditor;
  const handleCheck = () => onCheck?.(answers);

  const renderMistakeToken = (sentence, tok) => {
    const key = `${sentence.id}_${tok.mistakeIdx}`;
    const bag = answers[sentence.id] || {};
    const typed = bag[tok.mistakeIdx];
    const isEditingThis = editing.sentenceId === sentence.id && editing.mistakeIdx === tok.mistakeIdx;

    // Editor view: show the correct answer inline as a hint to the teacher.
    if (isEditor) {
      const correct = tok.corrections[0] || tok.text;
      return (
        <span
          key={key}
          className="inline-flex items-baseline rounded px-1"
          style={{
            border: `1.5px dashed ${highlightColor}`,
            color: highlightColor,
            fontWeight: 600,
          }}
          title={t('games.fixMistake.correctionHint')}
        >
          {correct}
        </span>
      );
    }

    // After check: show result coloring.
    if (showResult) {
      const perResults = perMistakeResults?.[sentence.id] || [];
      const wasCorrect = perResults[tok.mistakeIdx];
      const displayed = typed && typed.toString().trim() ? typed : tok.text;
      return (
        <span
          key={key}
          className="inline-flex items-baseline rounded px-1"
          style={{
            backgroundColor: wasCorrect ? '#dcfce7' : '#fee2e2',
            color: wasCorrect ? '#166534' : '#991b1b',
            border: `1.5px solid ${wasCorrect ? '#22c55e' : '#ef4444'}`,
            fontWeight: 600,
          }}
        >
          {displayed}
        </span>
      );
    }

    if (isEditingThis) {
      const width = Math.max(4, ((typed ?? tok.text) || '').length + 1);
      return (
        <input
          key={key}
          autoFocus
          type="text"
          value={typed ?? ''}
          placeholder={tok.text}
          onChange={(e) => handleInputChange(sentence.id, tok.mistakeIdx, e.target.value)}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          className="inline-block rounded px-1 focus:outline-none"
          style={{
            width: `${width}ch`,
            fontFamily,
            fontSize: `${fontSize}px`,
            color: textColor,
            border: `1.5px solid ${highlightColor}`,
            backgroundColor: '#ffffff',
            lineHeight: 1.2,
          }}
        />
      );
    }

    // Idle state: the word looks like any other word, but is clickable and,
    // if the student has already typed a replacement, shows that value with
    // a subtle underline so they can see they've committed a change.
    const hasEdit = typed && typed.toString().trim() && typed !== tok.text;
    const display = hasEdit ? typed : tok.text;
    return (
      <button
        type="button"
        key={key}
        onClick={() => handleWordClick(sentence.id, tok.mistakeIdx)}
        className="rounded px-1 cursor-pointer hover:bg-gray-100 transition"
        style={{
          display: 'inline',
          fontFamily,
          fontSize: `${fontSize}px`,
          color: textColor,
          lineHeight: 1.2,
          border: hasEdit ? `1.5px solid ${highlightColor}` : '1.5px solid transparent',
          backgroundColor: hasEdit ? `${highlightColor}14` : 'transparent',
          fontWeight: hasEdit ? 600 : 400,
        }}
      >
        {display}
      </button>
    );
  };

  const renderWordToken = (sentence, tok, key) => {
    // Non-mistake words are clickable too — clicking one that isn't the
    // planted mistake gives the student no input, but we highlight it briefly
    // to make it feel responsive. In the current MVP we just render as text.
    return (
      <span
        key={key}
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {tok.text}
      </span>
    );
  };

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        borderColor: showResult && !isEditor
          ? (allCorrect ? '#22c55e' : '#ef4444')
          : '#e5e7eb',
      }}
    >
      <div ref={scrollRef} className="flex-1 min-h-0 min-w-0 p-5 overflow-y-auto overflow-x-hidden">
        {question && (
          <p
            className="font-medium mb-3"
            style={{
              fontSize: `${fontSize}px`,
              fontFamily,
              color: textColor,
              lineHeight: 1.3,
            }}
          >
            {question}
          </p>
        )}

        {parsed.length === 0 ? (
          <p className="text-sm text-gray-400">
            {t('games.fixMistake.addSentenceHint')} <code>She [go|goes] to school every day.</code>
          </p>
        ) : (
          <div className="space-y-3">
            {parsed.map((s, idx) => (
              <div
                key={s.id}
                className="flex items-baseline gap-2"
                style={{
                  fontSize: `${fontSize}px`,
                  fontFamily,
                  color: textColor,
                  lineHeight: 1.9,
                }}
              >
                <span
                  className="flex-none text-gray-400 font-medium"
                  style={{ fontSize: `${Math.round(fontSize * 0.85)}px` }}
                >
                  {idx + 1}.
                </span>
                <p className="flex-1 min-w-0" style={{ margin: 0, overflowWrap: 'break-word' }}>
                  {s.parsed.tokens.length === 0 ? (
                    <span className="text-gray-400">{t('games.fixMistake.sentenceFallback', { index: idx + 1 })}</span>
                  ) : (
                    s.parsed.tokens.map((tok, i) => {
                      if (tok.type === 'mistake') return renderMistakeToken(s, tok);
                      if (tok.type === 'word') return renderWordToken(s, tok, `w_${s.id}_${i}`);
                      return (
                        <span key={`s_${s.id}_${i}`} style={{ whiteSpace: 'pre-wrap' }}>
                          {tok.text}
                        </span>
                      );
                    })
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isEditor && (
        <div
          className={`flex items-center justify-end gap-2 ${
            hideChrome || isActionBarTransparent ? '' : 'border-t border-gray-100'
          }`}
          style={{
            backgroundColor: hideChrome ? 'transparent' : actionBarColor,
            padding: '8px 12px',
          }}
        >
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allTouched}
              className="px-4 py-1.5 text-sm font-medium rounded-lg transition text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: allTouched ? buttonColor : undefined }}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const gradeFixMistake = (element, answers) => {
  const sentences = element?.content?.sentences || [];
  const perMistakeResults = {};
  let total = 0;
  let correct = 0;
  sentences.forEach((s) => {
    const { tokens, mistakeCount } = parseFixMistakeSentence(s.text || '');
    const mistakes = tokens.filter((t) => t.type === 'mistake');
    const bag = answers?.[s.id] || {};
    const results = [];
    for (let i = 0; i < mistakeCount; i += 1) {
      const tok = mistakes[i];
      const given = normalize(bag[i]);
      // The student must have typed something matching one of the corrections
      // (not the original wrong word).
      const correctOptions = tok.corrections.map(normalize);
      const isRight = given.length > 0
        && correctOptions.includes(given)
        && given !== normalize(tok.text);
      results.push(isRight);
      total += 1;
      if (isRight) correct += 1;
    }
    perMistakeResults[s.id] = results;
  });
  const isCorrect = total > 0 && correct === total;
  return { perMistakeResults, isCorrect };
};

export default FixMistakeElement;
